import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const initialFilters = {
  from_date: "",
  to_date: "",
  program_ids: [],
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const downloadFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function SummaryCard({ label, value, highlight = false }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: highlight ? "#1976d2" : "#d8dee8",
        backgroundColor: highlight ? "#eaf4ff" : "#fff",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={800} color={highlight ? "#0d47a1" : "text.primary"}>
        Rs. {formatAmount(value)}
      </Typography>
    </Paper>
  );
}

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  highlight: PropTypes.bool,
};

function ReportTable({ title, columns, rows, emptyMessage, totals, getRowId }) {
  const gridRows = rows.map((row, index) => ({
    id: getRowId ? getRowId(row, index) : `${title}-${index + 1}`,
    ...row,
  }));
  const gridColumns = columns.map((column) => ({
    ...column,
    align: column.align || "left",
    headerAlign: column.align || "left",
  }));

  return (
    <Box mt={2.5}>
      <Typography variant="subtitle1" color="#173b6c" fontWeight={800} mb={1}>
        {title}
      </Typography>
      <Box sx={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={gridRows}
          columns={gridColumns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          localeText={{ noRowsLabel: emptyMessage }}
          initialState={{
            pagination: {
              pageSize: 10,
            },
          }}
        />
      </Box>
      {!!totals?.length && (
        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="flex-end"
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: 1.5,
            py: 1,
            border: "1px solid #e5e7eb",
            borderTop: 0,
            borderRadius: "0 0 8px 8px",
            backgroundColor: "#f8fafc",
          }}
        >
          {totals.map((item) => (
            <Typography key={item.label} fontWeight={item.highlight ? 800 : 700}>
              {item.label}: Rs. {formatAmount(item.value)}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}

ReportTable.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      width: PropTypes.number,
      align: PropTypes.string,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  emptyMessage: PropTypes.string.isRequired,
  totals: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      highlight: PropTypes.bool,
    })
  ),
  getRowId: PropTypes.func,
};

export default function AllTypesFeesReport() {
  const [filters, setFilters] = useState(initialFilters);
  const [programs, setPrograms] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const programNameById = useMemo(() => {
    const map = new Map();
    programs.forEach((program) => {
      map.set(Number(program.program_id), program.program_name);
    });
    return map;
  }, [programs]);

  const showMessage = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      if (!clientId || !collegeId) return;
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/programs/${clientId}/${collegeId}`);
        setPrograms(data || []);
      } catch (err) {
        showMessage(err?.response?.data?.message || "Failed to load programs", "error");
      }
    };

    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, collegeId]);

  const handleProgramChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === "string" ? value.split(",") : value;

    if (selectedValues.includes("ALL")) {
      setFilters((prev) => ({ ...prev, program_ids: [] }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      program_ids: selectedValues.map(Number).filter(Boolean),
    }));
  };

  const buildParams = () => {
    if (!filters.from_date) {
      showMessage("Please select from date.", "warning");
      return null;
    }

    const params = {
      from_date: filters.from_date,
    };

    if (filters.to_date) params.to_date = filters.to_date;
    if (filters.program_ids.length) params.program_ids = filters.program_ids.join(",");

    return params;
  };

  const handleGenerate = async () => {
    const params = buildParams();
    if (!params) return;

    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/adv-exp-all-types-fees-report/${clientId}/${collegeId}`,
        { params }
      );
      setReport(data.data || null);
      showMessage("Report generated successfully.", "success");
    } catch (err) {
      setReport(null);
      showMessage(err?.response?.data?.message || "Failed to generate report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (type) => {
    const params = buildParams();
    if (!params) return;

    const isPdf = type === "pdf";
    if (isPdf) {
      setDownloadingPdf(true);
    } else {
      setDownloadingExcel(true);
    }

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/adv-exp-all-types-fees-report/download/${type}/${clientId}/${collegeId}`,
        {
          params,
          responseType: "blob",
        }
      );
      downloadFile(data, isPdf ? "all_types_fees_report.pdf" : "all_types_fees_report.xlsx");
      showMessage(`${isPdf ? "PDF" : "Excel"} downloaded successfully.`, "success");
    } catch (err) {
      showMessage(`Failed to download ${isPdf ? "PDF" : "Excel"}.`, "error");
    } finally {
      setDownloadingPdf(false);
      setDownloadingExcel(false);
    }
  };

  const totals = report?.totals || {};

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            ADVANCE & EXPENSES REPORTS
          </Typography>
        </Toolbar>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={2}>
          <TextField
            label="From Date"
            type="date"
            value={filters.from_date}
            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="medium"
            required
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={filters.to_date}
            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="medium"
            sx={{ minWidth: 160 }}
          />
          <TextField
            select
            label="Program"
            name="program"
            value={filters.program_ids}
            onChange={handleProgramChange}
            size="medium"
            fullWidth={false}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              minWidth: 180,

              // Same height as normal small TextField
              "& .MuiOutlinedInput-root": {
                minHeight: "40px",
                fontSize: "12px",
              },

              // Selected value
              "& .MuiSelect-select": {
                fontSize: "12px",
                paddingTop: "9px",
                paddingBottom: "9px",
              },

              // Floating label
              "& .MuiInputLabel-root": {
                fontSize: "12px",
              },

              "& .MuiInputLabel-shrink": {
                fontSize: "12px",
              },
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,

              renderValue: (selected) => {
                if (selected.length === 0) {
                  return "Select Program";
                }

                if (selected.length === 1) {
                  return programNameById.get(Number(selected[0])) || selected[0];
                }

                return `${programNameById.get(Number(selected[0])) || selected[0]} +${
                  selected.length - 1
                }`;
              },

              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 320,

                    "& .MuiMenuItem-root": {
                      fontSize: "13px",
                      minHeight: "36px",
                    },

                    "& .MuiCheckbox-root": {
                      padding: "4px",
                    },

                    "& .MuiListItemText-primary": {
                      fontSize: "13px",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="ALL">
              <Checkbox checked={filters.program_ids.length === 0} />

              <ListItemText
                primary="All Programs"
                primaryTypographyProps={{
                  fontSize: "13px",
                }}
              />
            </MenuItem>

            {programs.map((program) => (
              <MenuItem key={program.program_id} value={program.program_id}>
                <Checkbox checked={filters.program_ids.includes(Number(program.program_id))} />

                <ListItemText
                  primary={program.program_name}
                  primaryTypographyProps={{
                    fontSize: "13px",
                  }}
                />
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleGenerate}
            disabled={loading}
            sx={{ color: "#fff" }}
          >
            {loading ? <CircularProgress size={22} /> : "Generate Report"}
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => handleDownload("pdf")}
            disabled={downloadingPdf}
            sx={{ color: "#fff", backgroundColor: "#000", "&:hover": { backgroundColor: "#222" } }}
          >
            {downloadingPdf ? <CircularProgress size={22} /> : "Download PDF"}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload("excel")}
            disabled={downloadingExcel}
            sx={{ color: "#fff", backgroundColor: "#000", "&:hover": { backgroundColor: "#222" } }}
          >
            {downloadingExcel ? <CircularProgress size={22} /> : "Download Excel"}
          </Button>
        </Stack>

        <Divider />

        {report && (
          <Box mt={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 1.5,
                p: 1.5,
                mb: 2,
                border: "1px solid #dbeafe",
                borderRadius: 1,
                backgroundColor: "#f8fbff",
              }}
            >
              <SummaryCard label="Income Cash" value={totals.total_cash_income} />
              <SummaryCard label="Bank Income" value={totals.total_bank_income} />
              <SummaryCard label="Total Income" value={totals.total_income} />
              <SummaryCard label="Total Expenditure" value={totals.total_expenditure} />
              <SummaryCard label="Total Advance" value={totals.total_advance} />
              <SummaryCard label="Final Amount" value={totals.final_amount} highlight />
            </Box>

            <ReportTable
              title="INCOME"
              columns={[
                { field: "serial_no", headerName: "S.No", width: 70, align: "center" },
                {
                  field: "student_name",
                  headerName: "Student Name",
                  width: 220,
                  valueGetter: (params) => params.row.student_name || params.row.student_rollno,
                },
                { field: "program_name", headerName: "Course", width: 170 },
                { field: "year", headerName: "Year", width: 90 },
                {
                  field: "cash_amount",
                  headerName: "Cash",
                  width: 120,
                  align: "right",
                  valueFormatter: (params) => formatAmount(params.value),
                },
                {
                  field: "bank_amount",
                  headerName: "Bank Amount",
                  width: 140,
                  align: "right",
                  valueFormatter: (params) => formatAmount(params.value),
                },
                { field: "utr", headerName: "UTR", width: 160 },
                { field: "cashier_name", headerName: "Cashier Name", width: 170 },
              ]}
              totals={[
                { label: "Cash Income", value: totals.total_cash_income },
                { label: "Bank Income", value: totals.total_bank_income },
                { label: "Total Income", value: totals.total_income, highlight: true },
              ]}
              rows={report.income || []}
              emptyMessage="No income found"
              getRowId={(row, index) => `income-${row.fee_receipt_id || index}`}
            />

            <ReportTable
              title="EXPENDITURE"
              columns={[
                { field: "serial_no", headerName: "S.No", width: 70, align: "center" },
                { field: "voucher_bill", headerName: "Voucher/Bill", width: 220 },
                { field: "purpose", headerName: "Purpose", width: 360 },
                { field: "program_name", headerName: "Course", width: 200 },
                {
                  field: "amount",
                  headerName: "Amount",
                  width: 140,
                  align: "right",
                  valueFormatter: (params) => formatAmount(params.value),
                },
              ]}
              totals={[
                {
                  label: "Total Expenditure",
                  value: totals.total_expenditure,
                  highlight: true,
                },
              ]}
              rows={report.expenditure || []}
              emptyMessage="No expenditure found"
              getRowId={(row, index) => `expense-${row.expense_id || index}`}
            />

            <ReportTable
              title="ADVANCE"
              columns={[
                { field: "serial_no", headerName: "S.No", width: 70, align: "center" },
                { field: "name", headerName: "Name", width: 230 },
                { field: "purpose", headerName: "Purpose", width: 360 },
                {
                  field: "advance_recovery",
                  headerName: "Advance/Recovery",
                  width: 190,
                },
                {
                  field: "amount",
                  headerName: "Amount",
                  width: 140,
                  align: "right",
                  valueFormatter: (params) => formatAmount(params.value),
                },
              ]}
              totals={[
                { label: "Total Advance", value: totals.total_advance },
                {
                  label: "Advance Balance",
                  value: totals.total_advance_balance,
                  highlight: true,
                },
              ]}
              rows={report.advance || []}
              emptyMessage="No advance found"
              getRowId={(row, index) => `advance-${row.advance_id || index}`}
            />

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 1.5,
                textAlign: "right",
                borderColor: "#1976d2",
                backgroundColor: "#eaf4ff",
              }}
            >
              <Typography variant="h6" fontWeight={800} color="#0d47a1">
                Final Amount = Income - Expense - Advance: Rs. {formatAmount(totals.final_amount)}
              </Typography>
            </Paper>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
